import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { isToday } from '@shared/helpers/is-today';
import { CustomPointOfService } from '@shop/const';
import { ShopWorkingTimeService } from '@shop/feature';
import { YmapsService } from '@yandex-maps/feature';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

@Component({
  selector: 'sd-shops-map',
  templateUrl: './shops-map.component.html',
  styleUrls: ['./shops-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ShopsMapComponent implements OnInit, OnChanges, OnDestroy {
  @Input() shops: CustomPointOfService[];
  @Input() activeShopId: string;
  @Input() getShopPlacemarkProperties: (shop: CustomPointOfService) => unknown = this.getDefaultShopPlacemarkProperties;
  @Input() showBalloon = true;

  @Output() created = new EventEmitter<ymaps.Map>();
  @Output() placemarkShopClick = new EventEmitter<string>();

  shops$ = new BehaviorSubject<CustomPointOfService[] | undefined>(undefined);
  activeShopId$ = new BehaviorSubject<string | undefined>(undefined);

  shopGeoObjects$ = new BehaviorSubject<ymaps.Placemark[] | undefined>(undefined);

  private subscription = new Subscription();

  constructor(
    private ymapsService: YmapsService,
    private shopWorkingTimeService: ShopWorkingTimeService,
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.ymapsService.isLoaded$
        .pipe(
          filter(Boolean),
          switchMap(() => this.shops$),
          filter(Boolean),
        )
        .subscribe((shops) => {
          const placemarks = shops
            .map((shop: CustomPointOfService) => {
              if (shop.geoPoint) {
                return new ymaps.Placemark(
                  [shop.geoPoint?.latitude, shop.geoPoint?.longitude],
                  this.getShopPlacemarkProperties(shop),
                  {
                    preset: 'islands#yellowIcon',
                    hasBalloon: this.showBalloon,
                    iconColor: this.setBaloonColor(shop),
                  },
                );
              }
            })
            .filter((value): value is ymaps.Placemark => value !== undefined);

          placemarks.forEach((placemark) =>
            placemark.events.add('click', (event) => {
              const id = (<ymaps.Event>event)
                .get<ymaps.Placemark>('target')
                ?.properties.get<string | undefined>('id', undefined);
              this.placemarkShopClick.emit(id);
            }),
          );

          this.shopGeoObjects$.next(placemarks);
        }),
    );

    this.subscription.add(
      combineLatest([
        this.shopGeoObjects$.pipe(filter<ymaps.Placemark[] | undefined>(Boolean)),
        this.activeShopId$,
      ]).subscribe(([placemarks, activeShopId]) => {
        placemarks.forEach((placemark) => {
          const id = placemark.properties.get<string | undefined>('id', undefined);
          placemark.options.set('preset', id === activeShopId ? 'islands#yellowDotIcon' : 'islands#yellowIcon');
        });
      }),
    );
  }

  setBaloonColor(shop: CustomPointOfService): string {
    if (Object.prototype.hasOwnProperty.call(shop, 'available')) {
      return shop.available ? '#609B4B' : '#ffd21d';
    } else {
      const isTodayCheck = shop.pickupDate && isToday(shop.pickupDate);

      return isTodayCheck ? '#609B4B' : '#ffd21d';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.shops) {
      this.shops$.next(this.shops);
    }
    if (changes.activeShopId) {
      this.activeShopId$.next(this.activeShopId);
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onMapCreated(ymap: ymaps.Map): void {
    this.created.emit(ymap);
  }

  private getDefaultShopPlacemarkProperties(shop: CustomPointOfService): ymaps.IPlacemarkTemplate {
    const workingTime = this.shopWorkingTimeService.getWorkingTimeInfo(shop).text;

    return {
      balloonContentHeader: shop.addressFormatted,
      balloonContentFooter:
        `<div class="d-flex justify-content-between"><span>${workingTime}</span>` +
        `<span class="ymap-shop-available">${shop.available || ''} ${shop.unit?.name || ''}</span></span></div>`,
      clusterCaption: shop.addressFormatted,
      id: shop.name,
    };
  }
}
